import { useViewContext } from "@folksam/ui/providers/ViewContext";

import { Box } from "../../cores";
import { IKeyStringAndValue } from "../../types/Types";
import {
  BodyTypeDescription,
  Co2Description,
  EffectivityDescription,
  EnvironmentDescription,
  EuroNCapDescription,
  FolksamResultDescription,
  GuaranteeDescription,
  KidSafetyDescriptionTable,
  RiksbetygDescription,
  SafestChoiceDescription,
  SimpleDescription,
  TextWithColoredNumberAndAsteriskDescription,
} from "./descriptions";

type ContentDescriptionProp = {
  element: string;
  key: string;
  filterOptions?: IKeyStringAndValue;
  descriptionTitle: string;
  descriptionText: string;
};

const ContentDescription = ({
  element,
  filterOptions,
  descriptionTitle,
  descriptionText,
}: ContentDescriptionProp) => {
  const { isMobile } = useViewContext();
  const emptyFilterOptions = Object.keys(filterOptions || {}).length === 0;

  const descriptionSplit = descriptionText.split("&&");

  const SetDescrpitions = (element: string) => {
    const TextWithColoredNumberAndAsterisk = (paragraphAsterisk: boolean) => {
      return descriptionTitle && descriptionText ? (
        <TextWithColoredNumberAndAsteriskDescription
          descriptionTitle={descriptionTitle}
          descriptionText={descriptionSplit}
          paragraphAsterisk={paragraphAsterisk}
        />
      ) : null;
    };

    const FolksamResult = () => {
      return descriptionTitle && descriptionText ? (
        <FolksamResultDescription
          descriptionTitle={descriptionTitle}
          descriptionText={descriptionSplit}
        />
      ) : null;
    };

    const KidSafetyTable = () => {
      return descriptionTitle && descriptionText ? (
        <KidSafetyDescriptionTable
          descriptionTitle={descriptionTitle}
          descriptionText={descriptionText}
          isMobile={isMobile}
        />
      ) : null;
    };

    const EuroNCap = () => {
      return descriptionTitle && descriptionText ? (
        <EuroNCapDescription
          descriptionTitle={descriptionTitle}
          descriptionText={descriptionSplit}
        />
      ) : null;
    };

    const Co2 = () => {
      return descriptionTitle && descriptionText ? (
        <Co2Description
          descriptionTitle={descriptionTitle}
          descriptionText={descriptionText}
        />
      ) : null;
    };

    const Simple = (smallWidthDesktop: boolean) => {
      return descriptionTitle && descriptionText ? (
        <SimpleDescription
          heading={descriptionTitle}
          paragraph={descriptionText}
          isMobile={isMobile}
          smallWidthDesktop={smallWidthDesktop}
        />
      ) : null;
    };

    const BodyType = () => {
      return descriptionTitle && descriptionText ? (
        <BodyTypeDescription
          descriptionTitle={descriptionTitle}
          descriptionText={descriptionSplit}
        />
      ) : null;
    };

    const Riksbetyg = () => {
      return descriptionTitle && descriptionText ? (
        <RiksbetygDescription
          descriptionTitle={descriptionTitle}
          descriptionText={descriptionSplit}
        />
      ) : null;
    };

    //Do we still need this part?
    //case element.startsWith("Effektivitet") ? element : "":
    const Effectivity = () => {
      return descriptionTitle && descriptionText ? (
        <EffectivityDescription
          emptyFilterOptions={emptyFilterOptions}
          filterOptions={filterOptions}
          descriptionTitle={descriptionTitle}
          descriptionText={descriptionSplit}
        />
      ) : null;
    };

    const Environment = () => {
      return descriptionTitle && descriptionText ? (
        <EnvironmentDescription
          emptyFilterOptions={emptyFilterOptions}
          filterOptions={filterOptions}
          descriptionTitle={descriptionTitle}
          descriptionText={descriptionSplit}
        />
      ) : null;
    };

    const Guarantee = () => {
      return descriptionTitle && descriptionText ? (
        <GuaranteeDescription
          emptyFilterOptions={emptyFilterOptions}
          filterOptions={filterOptions}
          descriptionTitle={descriptionTitle}
          descriptionText={descriptionSplit}
        />
      ) : null;
    };

    const BraVal = () => {
      return descriptionTitle && descriptionText ? (
        <Box
          sx={{
            order: element === "good_choise" ? -1 : 0,
            width: "100%",
            "> div": {
              marginY: 10,
              "> *:first-of-type": {
                fontSize: ["xs", null, "16px", null, null],
              },
              "> span:first-of-type": {
                minWidth: "fit-content",
              },
              "> p": {
                marginRight: [null, null, null, "30%", null],
                marginLeft: "sm",
                fontSize: ["xs", null, "16px", null, null],
              },
            },
          }}
        >
          <SafestChoiceDescription
            heading={descriptionTitle}
            paragraph={descriptionText}
            isMobile={isMobile}
            noPaddingLeft={true}
            noMarginRight={element === "Braval" ? true : false}
          />
        </Box>
      ) : null;
    };

    const DefaultDescription = () => {
      return <></>;
    };

    const descriptionObjectLiterals: { [key: string]: JSX.Element | null } = {
      safetygradenumber: TextWithColoredNumberAndAsterisk(false),
      anti_slip: TextWithColoredNumberAndAsterisk(false),
      autobreak_car: TextWithColoredNumberAndAsterisk(false),
      airbag_passenger: TextWithColoredNumberAndAsterisk(false),
      autobreak: TextWithColoredNumberAndAsterisk(false),
      sidecurtain: TextWithColoredNumberAndAsterisk(false),
      ldw_lka: TextWithColoredNumberAndAsterisk(false),
      "LDW/LKA": TextWithColoredNumberAndAsterisk(false),
      antispin: TextWithColoredNumberAndAsterisk(false),
      whiplash: TextWithColoredNumberAndAsterisk(true),
      autobreak_pedestrian: TextWithColoredNumberAndAsterisk(false),
      autobreak_bike: TextWithColoredNumberAndAsterisk(false),

      spacegrade: Simple(false),
      spacegrade_max: Simple(false),
      spacegrade_min: Simple(false),
      "climate-bonus": Simple(false),
      climate_bonus_car: Simple(false),
      traction_weight: Simple(true),
      reach: Simple(true),
      Batteristorlek_brutto: Simple(true),
      Laddeffekt_ombord: Simple(true),
      Laddeffekt_snabb: Simple(true),
      make: Simple(true),
      model: Simple(true),
      kwh_mil: Simple(true),
      antal_celler: Simple(true),
      markeffekt: Simple(true),
      verkningsgrad_stc: Simple(true),
      noct_maxeffekt: Simple(true),
      tillverkningsland: Simple(true),
      produktgaranti: Simple(true),
      utseende: Simple(true),
      typ: Simple(true),
      pv_cycle: Simple(true),

      folksam_result: FolksamResult(),
      kid_safety: KidSafetyTable(),
      euroncap: EuroNCap(),
      co2: Co2(),
      body_type: BodyType(),
      riksbetyg: Riksbetyg(),
      Effektivitet: Effectivity(),
      Miljo: Environment(),
      Garanti: Guarantee(),
      Funktion: Guarantee(),
      Braval: BraVal(),
      braval: BraVal(),
      safest_choice: BraVal(),
      good_choise: BraVal(),

      "Energibesparing (kWh)": Simple(true),
      "Kostnadsbesparing (kr)": Simple(true),
      "Utsläpp av växthusgaser (kg)": Simple(true),
      "Återbetalningstid (år)": Simple(true),
      Elområde: Simple(true),
    };

    return descriptionObjectLiterals[element] ?? DefaultDescription();
  };
  return SetDescrpitions(element);
};
export default ContentDescription;
